import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Person } from '../lib/api.js';

/**
 * The people in the record. Release 2 of 2.0 (docs/PRD-2.0.md §4).
 *
 * Built from what he writes: the summariser hands back the names an entry
 * mentions and the app files them against the day as its picks. This list is
 * his to tidy — rename, merge two spellings into one, hide someone — and
 * nothing on it ever reaches a share link: no grant key names these tables.
 *
 * Counts are days, never a score of anything. A person with one day is not
 * less of a person; the sort is only so the people he writes about most are
 * at the top where he will look for them.
 */
export function People(): JSX.Element {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [merging, setMerging] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);

  const load = (): void => {
    void api.people(showHidden).then(setPeople).catch(() => setPeople([]));
  };
  useEffect(load, [showHidden]);

  async function act(f: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    try {
      await f();
      load();
    } finally {
      setBusy(false);
      setRenaming(null);
      setMerging(null);
    }
  }

  return (
    <div className="stack-lg stack">
      <Link className="linkish" to="/">
        ‹ Today
      </Link>
      <header className="page-header">
        <h1>People</h1>
        <p>
          Who is in your record, filed from what you write. Yours to rename, merge or hide. Nobody here is
          ever on a link.
        </p>
      </header>

      <form
        className="row"
        style={{ gap: 'var(--space-2)' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!adding.trim()) return;
          void act(() => api.createPerson(adding.trim())).then(() => setAdding(''));
        }}
      >
        <label htmlFor="add-person" className="sr-only">
          Add someone
        </label>
        <input
          id="add-person"
          className="input grow"
          value={adding}
          maxLength={40}
          placeholder="Add someone by name"
          onChange={(e) => setAdding(e.target.value)}
          disabled={busy}
        />
        <button type="submit" className="btn btn-secondary" disabled={busy || !adding.trim()}>
          Add
        </button>
      </form>

      {people === null ? (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '20dvh' }}>
          <span className="spinner" />
        </div>
      ) : people.length === 0 ? (
        <p className="muted">
          Nobody yet. Names you mention in an entry are filed here once it is summarised, or add someone
          above.
        </p>
      ) : (
        <ul className="stack-sm stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {people.map((p) => (
            <li key={p.id} className="card stack-sm stack person-row">
              <div className="row-between">
                {renaming === p.id ? (
                  <form
                    className="row grow"
                    style={{ gap: 'var(--space-2)' }}
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (draft.trim()) void act(() => api.updatePerson(p.id, { name: draft.trim() }));
                    }}
                  >
                    <input className="input grow" value={draft} maxLength={40} onChange={(e) => setDraft(e.target.value)} autoFocus />
                    <button type="submit" className="btn btn-secondary btn-compact" disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="btn btn-ghost btn-compact" onClick={() => setRenaming(null)}>
                      Keep
                    </button>
                  </form>
                ) : (
                  <Link to={`/people/${p.id}`} className="person-name">
                    <strong>{p.name}</strong>
                    {p.hidden && <span className="faint"> · hidden</span>}
                  </Link>
                )}
                <span className="faint tabular">
                  {p.days} {p.days === 1 ? 'day' : 'days'}
                  {p.lastDay ? ` · last ${shortDate(p.lastDay)}` : ''}
                </span>
              </div>
              {p.aliases.length > 0 && <p className="faint" style={{ margin: 0 }}>also {p.aliases.join(', ')}</p>}
              {merging === p.id ? (
                <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <span className="faint">Merge {p.name} into:</span>
                  {people
                    .filter((o) => o.id !== p.id)
                    .map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className="chip"
                        disabled={busy}
                        onClick={() => void act(() => api.mergePerson(p.id, o.id))}
                      >
                        {o.name}
                      </button>
                    ))}
                  <button type="button" className="btn btn-ghost btn-compact" onClick={() => setMerging(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-ghost btn-compact"
                    onClick={() => {
                      setRenaming(p.id);
                      setDraft(p.name);
                    }}
                  >
                    Rename
                  </button>
                  {people.length > 1 && (
                    <button type="button" className="btn btn-ghost btn-compact" onClick={() => setMerging(p.id)}>
                      Merge…
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost btn-compact"
                    disabled={busy}
                    onClick={() => void act(() => api.updatePerson(p.id, { hidden: !p.hidden }))}
                  >
                    {p.hidden ? 'Show' : 'Hide'}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <button type="button" className="linkish linkish-quiet" onClick={() => setShowHidden((v) => !v)}>
        {showHidden ? 'Without the hidden ones' : 'Show hidden people'}
      </button>
      <p className="faint">
        Hiding someone keeps the days they were in; it only takes them off this list and off Today. Merging
        moves one name's days onto another and keeps the old spelling as an alias, so the next entry that
        uses it files to the right person.
      </p>
    </div>
  );
}

function shortDate(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
